/*
 * keetsa-scrollspy
 *
 * Sticky in-page navigation that scrolls to other sections on the same template
 * and tracks the visible section by setting aria-current on the matching link.
 *
 * Defensive design: warn-and-skip on missing targets, full teardown in
 * disconnectedCallback so theme-editor section reloads don't leak listeners or
 * observers, and reduced-motion-aware scrolling.
 */

class KeetsaScrollspy extends HTMLElement {
  constructor() {
    super();
    this._onClick = this.onClick.bind(this);
    this._onResize = this.onResize.bind(this);
    this._onScroll = this.onScroll.bind(this);
    this._observer = null;
    this._targets = []; // [{ link, target }]
    this._resizeRaf = 0;
    this._scrollRaf = 0;
    this._activeLink = null;
    this._reducedMotionMql = null;
    // Stuck-state machinery
    this._stickyParent = null;       // <section.keetsa-scrollspy-wrapper>, the actual sticky element
    this._schemeHost = null;         // <div.keetsa-scrollspy-section>, where color-{scheme} lives
    this._defaultScheme = '';
    this._stickyScheme = '';
    this._isStuck = false;
  }

  connectedCallback() {
    const linkEls = this.querySelectorAll('[data-keetsa-scrollspy-link]');
    if (!linkEls.length) return;

    linkEls.forEach((link) => {
      const href = link.getAttribute('href');
      if (!href || href.charAt(0) !== '#' || href.length < 2) return;
      let target = document.getElementById(href.slice(1));

      // Fallback: if the literal id miss occurs (e.g., the Liquid prefix
      // derivation didn't match this rendering context), suffix-match against
      // the merchant-entered id stored on the data attribute. This handles
      // section-id namespacing differences across templates and section groups
      // without requiring the merchant to know the prefix format.
      if (!target) {
        const suffix = link.getAttribute('data-keetsa-target-suffix');
        if (suffix) {
          // CSS attribute selector: id ends with "__<suffix>" OR equals
          // "shopify-section-<suffix>" (legacy non-namespaced format).
          const escapedSuffix = window.CSS && CSS.escape ? CSS.escape(suffix) : suffix;
          target =
            document.querySelector('[id$="__' + escapedSuffix + '"]') ||
            document.getElementById('shopify-section-' + suffix);
        }
      }

      if (!target) {
        if (typeof console !== 'undefined' && console.warn) {
          console.warn('[keetsa-scrollspy] target not found for link:', href);
        }
        return;
      }

      // Re-point the link's href to the actual element so native anchor
      // navigation (right-click, copy link, deep link) works.
      if (target.id && target.id !== href.slice(1)) {
        link.setAttribute('href', '#' + target.id);
      }

      this._targets.push({ link, target });
    });

    if (this._targets.length === 0) return;

    this._reducedMotionMql = window.matchMedia('(prefers-reduced-motion: reduce)');

    // Stuck-detection wiring: only register the scroll listener if the merchant
    // actually configured a different sticky scheme. Same scheme = no work.
    this._stickyParent = this.closest('.keetsa-scrollspy-wrapper');
    this._schemeHost = this.closest('[data-keetsa-default-scheme]');
    if (this._schemeHost) {
      this._defaultScheme = this._schemeHost.getAttribute('data-keetsa-default-scheme') || '';
      this._stickyScheme = this._schemeHost.getAttribute('data-keetsa-sticky-scheme') || '';
    }
    this.addEventListener('click', this._onClick);
    window.addEventListener('resize', this._onResize, { passive: true });
    // Always observe stuck state when the sticky wrapper is present — the
    // is-stuck class drives both the optional scheme swap AND the always-on
    // sticky drop shadow. setStuck() is a no-op when fromScheme === toScheme.
    if (this._stickyParent) {
      window.addEventListener('scroll', this._onScroll, { passive: true });
      // Initial check in case the page loads scrolled (deep link, refresh).
      this.onScroll();
    }

    this.applyScrollMargin();
    this.setupObserver();
    this.syncFromHash();
  }

  disconnectedCallback() {
    this.removeEventListener('click', this._onClick);
    window.removeEventListener('resize', this._onResize);
    window.removeEventListener('scroll', this._onScroll);
    if (this._observer) {
      this._observer.disconnect();
      this._observer = null;
    }
    if (this._resizeRaf) {
      cancelAnimationFrame(this._resizeRaf);
      this._resizeRaf = 0;
    }
    if (this._scrollRaf) {
      cancelAnimationFrame(this._scrollRaf);
      this._scrollRaf = 0;
    }
    this._targets = [];
    this._activeLink = null;
    this._reducedMotionMql = null;
    this._stickyParent = null;
    this._schemeHost = null;
  }

  getStickyOffset() {
    // Combined offset: main header + this sticky region + small breathing room.
    // The actual sticky element is the .keetsa-scrollspy-wrapper section ancestor
    // (set in CSS), so its offsetHeight is the visible height while sticking.
    // Fall back to self.offsetHeight if the wrapper class isn't present.
    const rootStyle = getComputedStyle(document.documentElement);
    const rawHeader = rootStyle.getPropertyValue('--header-height');
    const headerH = parseFloat(rawHeader) || 0;
    const stickyParent = this.closest('.keetsa-scrollspy-wrapper');
    const barH = (stickyParent || this).offsetHeight || 0;
    return headerH + barH + 8;
  }

  applyScrollMargin() {
    const offset = this.getStickyOffset();
    this._targets.forEach(({ target }) => {
      target.style.scrollMarginTop = offset + 'px';
    });
  }

  onResize() {
    if (this._resizeRaf) cancelAnimationFrame(this._resizeRaf);
    this._resizeRaf = requestAnimationFrame(() => {
      this.applyScrollMargin();
      // Recreate the observer with the new offset so the active band stays
      // aligned to the (possibly resized) sticky header + bar.
      this.setupObserver();
      // Sticky line moves with header height; re-check stuck state.
      if (this._stickyParent) this.applyStuckState();
      this._resizeRaf = 0;
    });
  }

  onScroll() {
    if (this._scrollRaf) return;
    this._scrollRaf = requestAnimationFrame(() => {
      this.applyStuckState();
      this._scrollRaf = 0;
    });
  }

  applyStuckState() {
    if (!this._stickyParent || !this._schemeHost) return;
    const rootStyle = getComputedStyle(document.documentElement);
    const headerH = parseFloat(rootStyle.getPropertyValue('--header-height')) || 0;
    // The CSS top:calc(--header-height + --keetsa-scrollspy-extra-offset).
    // Read both for the actual sticky line.
    const sectionStyle = getComputedStyle(this._schemeHost.parentElement || this._stickyParent);
    const extraOffset = parseFloat(sectionStyle.getPropertyValue('--keetsa-scrollspy-extra-offset')) || 0;
    const stickyTop = headerH + extraOffset;
    const rect = this._stickyParent.getBoundingClientRect();
    // 1px fudge to avoid sub-pixel flicker at the boundary.
    const isStuck = rect.top <= stickyTop + 1;
    if (isStuck !== this._isStuck) this.setStuck(isStuck);
  }

  setStuck(isStuck) {
    this._isStuck = isStuck;
    if (!this._schemeHost) return;
    const fromScheme = isStuck ? this._defaultScheme : this._stickyScheme;
    const toScheme = isStuck ? this._stickyScheme : this._defaultScheme;
    if (fromScheme) this._schemeHost.classList.remove('color-' + fromScheme);
    if (toScheme) this._schemeHost.classList.add('color-' + toScheme);
    this._stickyParent.classList.toggle('is-stuck', isStuck);
  }

  setupObserver() {
    if (this._observer) {
      this._observer.disconnect();
      this._observer = null;
    }
    if (typeof IntersectionObserver === 'undefined') return;

    const offset = this.getStickyOffset();
    // Thin band immediately under the sticky bar. A section becomes active
    // when its top edge enters this band — i.e., when its heading reaches the
    // top of the visible content area.
    const rootMargin = '-' + offset + 'px 0px -85% 0px';

    this._observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          const match = this._targets.find((t) => t.target === entry.target);
          if (match) this.setActive(match.link);
        });
      },
      { rootMargin: rootMargin, threshold: 0 }
    );

    this._targets.forEach(({ target }) => this._observer.observe(target));
  }

  setActive(link) {
    if (link === this._activeLink) return;
    this._activeLink = link;
    this._targets.forEach(({ link: candidate }) => {
      if (candidate === link) {
        candidate.setAttribute('aria-current', 'true');
      } else {
        candidate.removeAttribute('aria-current');
      }
    });
  }

  syncFromHash() {
    const hash = window.location.hash;
    if (!hash) return;
    const match = this._targets.find(({ link }) => link.getAttribute('href') === hash);
    if (match) this.setActive(match.link);
  }

  onClick(event) {
    const eventTarget = event.target;
    if (!(eventTarget instanceof Element)) return;
    const link = eventTarget.closest('[data-keetsa-scrollspy-link]');
    if (!link || !this.contains(link)) return;

    const href = link.getAttribute('href');
    if (!href || href.charAt(0) !== '#' || href.length < 2) return;
    const target = document.getElementById(href.slice(1));
    if (!target) return;

    event.preventDefault();

    if (window.history && typeof history.replaceState === 'function') {
      try {
        history.replaceState(null, '', href);
      } catch (err) {
        // Some browsers throw on file:// or sandboxed contexts — non-fatal.
      }
    }

    // Snappy feedback: set active before scroll completes.
    this.setActive(link);

    const reduceMotion = this._reducedMotionMql && this._reducedMotionMql.matches;
    target.scrollIntoView({
      block: 'start',
      behavior: reduceMotion ? 'auto' : 'smooth',
    });

    // Prefer focusing the section's heading so screen readers announce
    // "<heading>, level N" rather than the wrapper's full content.
    const focusEl = target.querySelector('h1, h2, h3') || target;
    this.focusTarget(focusEl);
  }

  focusTarget(target) {
    // Make the target programmatically focusable just long enough for screen
    // readers to announce it. Restore prior tabindex (or remove ours) on blur.
    const hadTabindex = target.hasAttribute('tabindex');
    const prevTabindex = hadTabindex ? target.getAttribute('tabindex') : null;
    if (!hadTabindex) target.setAttribute('tabindex', '-1');

    try {
      target.focus({ preventScroll: true });
    } catch (err) {
      try {
        target.focus();
      } catch (innerErr) {
        // Last-ditch swallow — never throw from a click handler.
      }
    }

    const cleanup = () => {
      if (!hadTabindex) {
        target.removeAttribute('tabindex');
      } else if (prevTabindex !== null) {
        target.setAttribute('tabindex', prevTabindex);
      }
      target.removeEventListener('blur', cleanup);
    };
    target.addEventListener('blur', cleanup);
  }
}

if (!customElements.get('keetsa-scrollspy')) {
  customElements.define('keetsa-scrollspy', KeetsaScrollspy);
}
